import { useEffect, useState } from "react";
import { useFeedbackFlow } from "../hooks/use-feedback-flow";
import { useUnsavedChanges } from "../hooks/use-unsaved-changes";
import IconArrowLeftLane from "@/components/icons/icon-arrow-left-lane";
import StepRating from "./steps/step-rating";
import StepNegativeFeedback from "./steps/step-negative-feedback";
import StepMotivation from "./steps/step-motivation";
import StepTestimonialIntro from "./steps/step-testimonial-intro";
import StepTestimonialQ1 from "./steps/step-testimonial-q1";
import StepTestimonialQ2 from "./steps/step-testimonial-q2";
import StepConsent from "./steps/step-consent";
import StepAboutUser from "./steps/step-about-user";
import StepAboutCompany from "./steps/step-about-company";
import StepThankYou from "./steps/step-thank-you";
import Button from "@/components/ui/button";

interface FeedbackFlowProps {
	appName: string;
}

export default function FeedbackFlow({ appName }: FeedbackFlowProps) {
	const { currentStep, reset, prevStep, canGoBack } = useFeedbackFlow();
	const [displayedStep, setDisplayedStep] = useState(currentStep);
	const [isVisible, setIsVisible] = useState(true);

	// Activate unsaved changes warning
	useUnsavedChanges();

	// Reset flow on mount to ensure clean state
	useEffect(() => {
		reset();
	}, [reset]);

	// Handle step transition: fade out -> swap content -> fade in
	useEffect(() => {
		if (currentStep !== displayedStep) {
			// Start fade out
			setIsVisible(false);
			// After fade out completes, swap content and fade in
			const timer = setTimeout(() => {
				setDisplayedStep(currentStep);
				setIsVisible(true);
			}, 200);
			return () => clearTimeout(timer);
		}
	}, [currentStep, displayedStep]);

	const renderStep = () => {
		switch (displayedStep) {
			case "rating":
				return <StepRating appName={appName} />;
			case "negative-feedback":
				return <StepNegativeFeedback />;
			case "motivation":
				return <StepMotivation />;
			case "testimonial-intro":
				return <StepTestimonialIntro />;
			case "testimonial-q1":
				return <StepTestimonialQ1 />;
			case "testimonial-q2":
				return <StepTestimonialQ2 />;
			case "consent":
				return <StepConsent />;
			case "about-user":
				return <StepAboutUser />;
			case "about-company":
				return <StepAboutCompany />;
			case "thank-you":
				return <StepThankYou />;
			default:
				return null;
		}
	};

	const showBackButton = canGoBack();

	// Use wider container for testimonial steps
	const isWideStep = displayedStep === "testimonial-intro" || displayedStep === "testimonial-q1" || displayedStep === "testimonial-q2";
	const containerClass = isWideStep ? "w-full max-w-4xl mx-auto" : "w-full max-w-xl min-w-xl mx-auto";

	return (
		<div className={`${containerClass} transition-[opacity,transform] duration-200 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-3"}`}>
			<div className="h-10 mb-4">
				{showBackButton && (
					<Button type="button" onClick={prevStep} variant="soft" className="flex items-center gap-1.5 text-secondary hover:text-primary transition-colors">
						<IconArrowLeftLane className="w-4 h-4" />
					</Button>
				)}
			</div>
			<div className="rounded-xl w-full">{renderStep()}</div>
		</div>
	);
}
